import { getBlob, ref } from 'firebase/storage';
import { storage } from '../firebase';

/**
 * SEC-3: open a stored PHI file (medical document / wearable export) through
 * the AUTHENTICATED, rule-enforced Storage path instead of a permanent
 * `getDownloadURL()` token.
 *
 * `getDownloadURL()` mints a `?token=` URL that bypasses storage.rules
 * entirely, never expires, and is shareable by anyone who obtains it — so we
 * no longer create or persist those. `getBlob()` issues an authenticated
 * request that storage.rules governs live (owner, or the member's assigned
 * care team), and returns bytes with no shareable URL to leak.
 *
 * We open a blank tab *synchronously* inside the click gesture — so the popup
 * blocker does not intercept it — and only then navigate it to a transient
 * object URL, which we revoke shortly after. If the tab was blocked anyway we
 * fall back to a direct download in the current tab.
 *
 * Requires the Storage bucket to allow the app origin via CORS (authenticated
 * `getBlob` uses XHR, unlike a plain `<a href>` to a token URL). See the SEC-3
 * remediation notes for the one-time `gsutil cors set` step per environment.
 */
export async function viewStoredFile(storagePath: string): Promise<void> {
  // Opened here, synchronously, so it counts as user-initiated and is not
  // popup-blocked once the async getBlob() resolves. Not `noopener` — that
  // would sever the handle we need to navigate; the blob is same-origin.
  const viewer = window.open('about:blank', '_blank');
  try {
    const blob = await getBlob(ref(storage, storagePath));
    const objectUrl = URL.createObjectURL(blob);
    if (viewer) {
      viewer.location.href = objectUrl;
    } else {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = storagePath.split('/').pop() || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    // Give the viewer tab time to load the bytes before releasing them.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    if (viewer) viewer.close();
    throw error;
  }
}
